/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Settings on a phone: the eight sections as a menu, in their four groups.
 *
 * The wide screen shows the sections as a tab strip and the narrow one used
 * to scroll that same strip sideways behind arrows. Eight labels in a 343px
 * scroller shows three at a time with no indication of what the other five
 * are, and the group each belongs to — which is the only thing that makes
 * "Portais" and "Permissões" distinguishable at a glance — disappears
 * entirely. A menu has room to say both, and to say which ones need
 * attention before you open them.
 *
 * The groups are not declared here: they are the `group` each item already
 * carries in the settings registry, so a section that moves group on the
 * desktop moves here too.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRightIcon } from '@/shared/ui/radix-icons';
import { Text, UnstyledButton } from '@/shared/ui';
import {
  getSettingsNav,
  type SettingsNavOptions,
  type SettingsSection,
} from '../constants/settings-nav';
import { SETTINGS_SECTION_ICONS } from '../constants/settings-icons';
import type { SettingsPendingItem } from '../utils/build-settings-pending';

interface SettingsMobileMenuProps {
  onNavigate: (section: SettingsSection) => void;
  navOptions: SettingsNavOptions;
  /**
   * What is outstanding, already localized. A section with an entry shows
   * that text as its summary instead of its description: "falta CRECI e
   * E-mail" is the state of the section, and the description is only what
   * to say when there is no state worth reporting.
   */
  pendingItems: SettingsPendingItem[];
}

export function SettingsMobileMenu({
  onNavigate, navOptions, pendingItems,
}: SettingsMobileMenuProps) {
  const { t } = useTranslation('features/agency-settings');

  const pendingBySection = useMemo(() => {
    const map = new Map<SettingsSection, string[]>();
    for (const item of pendingItems) {
      map.set(item.section, [...(map.get(item.section) ?? []), item.label]);
    }
    return map;
  }, [pendingItems]);

  /* Grouped in registry order, so the groups appear as the registry lists them. */
  const groups = useMemo(() => {
    const byGroup = new Map<string, ReturnType<typeof getSettingsNav>>();
    for (const item of getSettingsNav(t, navOptions)) {
      const bucket = byGroup.get(item.group) ?? [];
      bucket.push(item);
      byGroup.set(item.group, bucket);
    }
    return [...byGroup.entries()];
  }, [t, navOptions]);

  return (
    <div data-testid="settings-mobile-menu" className="flex flex-col gap-(--space-5)">
      {groups.map(([group, items]) => (
        <div key={group} className="flex flex-col gap-(--space-2)">
          <Text
            component="h2"
            className="text-(length:--text-body-sm) text-[var(--text-tertiary)]"
            style={{ fontWeight: 'var(--font-semibold)' as unknown as number }}
          >
            {group}
          </Text>
          <div
            className="flex flex-col overflow-hidden rounded-(--radius-lg) border border-[var(--border-subtle)]"
            style={{ background: 'var(--surface-card)' }}
          >
            {items.map((item, index) => (
              <SettingsRow
                key={item.key}
                section={item.key}
                label={item.label}
                summary={pendingBySection.get(item.key)?.join(' · ')
                  ?? t(`v2.nav.summaries.${item.key}`, { defaultValue: '' })}
                pending={pendingBySection.has(item.key)}
                first={index === 0}
                onSelect={() => onNavigate(item.key)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

interface SettingsRowProps {
  section: SettingsSection;
  label: string;
  summary: string;
  pending: boolean;
  first: boolean;
  onSelect: () => void;
}

const SettingsRow: React.FC<SettingsRowProps> = ({
  section, label, summary, pending, first, onSelect,
}) => {
  const Icon = SETTINGS_SECTION_ICONS[section];

  return (
    <UnstyledButton
      data-testid={`settings-menu-${section}`}
      onClick={onSelect}
      className="flex w-full items-center gap-(--space-3) px-(--space-3) text-left"
      style={{
        minHeight: 58,
        background: 'var(--surface-primary)',
        borderTop: first ? 'none' : '1px solid var(--border-ultra-subtle)',
      }}
    >
      <Text
        component="span"
        aria-hidden="true"
        className="flex shrink-0 items-center justify-center rounded-(--radius-md)"
        style={{
          width: 34,
          height: 34,
          background: 'var(--surface-tertiary)',
          color: 'var(--text-secondary)',
        }}
      >
        <Icon width={17} height={17} />
      </Text>

      <Text component="span" className="flex min-w-0 flex-1 flex-col gap-px">
        <Text
          component="span"
          className="truncate text-(length:--text-body-sm) text-[var(--text-primary)]"
          style={{ fontWeight: 'var(--font-semibold)' as unknown as number }}
        >
          {label}
        </Text>
        {summary && (
          <Text
            component="span"
            data-testid={`settings-menu-summary-${section}`}
            className="truncate text-(length:--text-meta)"
            style={{ color: pending ? 'var(--accent-cobre)' : 'var(--text-tertiary)' }}
          >
            {summary}
          </Text>
        )}
      </Text>

      <Text component="span" aria-hidden="true" className="shrink-0 text-[var(--text-tertiary)]">
        <ChevronRightIcon width={16} height={16} />
      </Text>
    </UnstyledButton>
  );
};

export default SettingsMobileMenu;
